import { useEffect, useState } from "react";
import { engineeringApi, aiApi } from "@/api";
import { FatigueStatusBadge, ScoreDisplay } from "@/components/FatigueUtils";
import { Search, X, Brain, RefreshCw, AlertTriangle, Wrench } from "lucide-react";

const criticalityStyle = {
  critical: "text-red-400 bg-red-400/10 border border-red-400/20",
  high: "text-orange-400 bg-orange-400/10 border border-orange-400/20",
  medium: "text-amber-400 bg-amber-400/10 border border-amber-400/20",
  low: "text-slate-400 bg-slate-400/10 border border-slate-400/20",
};

function EngineerModal({ engineer, onClose }) {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const r = await aiApi.analyze({ person_type: "engineer", person_id: engineer.id });
      setAiAnalysis(r.data.analysis);
    } catch { setAiAnalysis("AI analysis unavailable."); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="engineer-detail-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="font-bold font-heading text-foreground">{engineer.name}</h3>
            <div className="text-xs text-muted-foreground">{engineer.employee_id} · {engineer.license_type} · {engineer.specialization}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              ["Fatigue Score", engineer.fatigue_score],
              ["Hours (7d)", `${engineer.hours_last_7d}h`],
              ["Consec. Nights", engineer.consecutive_night_duties],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bg-background border border-border rounded-sm p-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">{lbl}</div>
                <div className={`text-xl font-mono font-medium ${lbl === "Fatigue Score" ? (engineer.fatigue_score >= 61 ? "text-red-400" : engineer.fatigue_score >= 31 ? "text-amber-400" : "text-green-400") : "text-foreground"}`}>{val}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <FatigueStatusBadge status={engineer.status} />
            <span className="text-xs text-muted-foreground">{engineer.shift_type} shift · {engineer.critical_tasks_assigned} critical tasks</span>
          </div>
          {engineer.status === "high_risk" || engineer.status === "do_not_assign" ? (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
              <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <div className="text-xs text-red-300">Sign-off protection active. This engineer should not be assigned to critical tasks until fatigue score reduces below 61.</div>
            </div>
          ) : null}
          <div className="border border-border rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5"><Brain size={12} /> AI Analysis</div>
              <button onClick={runAI} disabled={aiLoading} className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="engineer-run-ai">
                <RefreshCw size={10} className={aiLoading ? "animate-spin" : ""} />
                {aiLoading ? "Analysing..." : "Run Analysis"}
              </button>
            </div>
            {aiAnalysis ? (
              <p className="text-xs text-muted-foreground leading-relaxed">{aiAnalysis}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic">Click Run Analysis for AI assessment.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EngineeringRisk() {
  const [engineers, setEngineers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedEng, setSelectedEng] = useState(null);
  const [activeTab, setActiveTab] = useState("engineers");

  useEffect(() => {
    Promise.all([engineeringApi.getEngineers(), engineeringApi.getTasks()])
      .then(([e, t]) => { setEngineers(e.data); setTasks(t.data); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = engineers.filter(e =>
    (!search || e.name.toLowerCase().includes(search.toLowerCase())) &&
    (!filterStatus || e.status === filterStatus)
  );

  const statusCounts = engineers.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="engineering-risk-board">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 3</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Engineering / AME Fatigue</h1>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[["Fit", statusCounts.fit || 0, "text-green-400"], ["Caution", statusCounts.caution || 0, "text-amber-400"], ["High Risk", statusCounts.high_risk || 0, "text-red-400"], ["Do Not Assign", statusCounts.do_not_assign || 0, "text-red-300"]].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-3">
            <div className={`text-2xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">{lbl}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border">
        {[["engineers", "Engineers", Wrench], ["tasks", "Maintenance Tasks", AlertTriangle]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${activeTab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-${key}`}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {activeTab === "engineers" && (
        <>
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search engineers..."
                className="bg-card border border-border rounded-sm pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary w-52"
                data-testid="engineer-search" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="engineer-status-filter">
              <option value="">All Status</option>
              <option value="fit">Fit</option>
              <option value="caution">Caution</option>
              <option value="high_risk">High Risk</option>
              <option value="do_not_assign">Do Not Assign</option>
            </select>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="engineers-table">
              <thead>
                <tr className="border-b border-border">
                  {["Engineer", "Specialization", "License", "Shift", "Score", "Status", "Hours (7d)", "Night Consec.", "Crit. Tasks", "Action"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(eng => (
                  <tr key={eng.id} className="border-b border-border/50 row-hover cursor-pointer" onClick={() => setSelectedEng(eng)} data-testid={`eng-row-${eng.employee_id}`}>
                    <td className="px-3 py-2.5">
                      <div className="text-xs font-medium text-foreground">{eng.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{eng.employee_id}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{eng.specialization}</td>
                    <td className="px-3 py-2.5 text-xs font-mono text-foreground">{eng.license_type}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">{eng.shift_type}</td>
                    <td className="px-3 py-2.5"><ScoreDisplay score={eng.fatigue_score} /></td>
                    <td className="px-3 py-2.5"><FatigueStatusBadge status={eng.status} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs text-foreground">{eng.hours_last_7d}h</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-foreground">{eng.consecutive_night_duties}</td>
                    <td className="px-3 py-2.5">
                      <span className={`font-mono text-xs font-bold ${eng.critical_tasks_assigned > 2 ? "text-red-400" : "text-foreground"}`}>{eng.critical_tasks_assigned}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <button className="text-xs text-primary hover:underline" data-testid={`view-eng-${eng.employee_id}`}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "tasks" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="tasks-table">
            <thead>
              <tr className="border-b border-border">
                {["Task ID", "Title", "Aircraft", "Criticality", "Category", "Status", "Due"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => (
                <tr key={task.id} className="border-b border-border/50 row-hover" data-testid={`task-row-${task.task_id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-primary">{task.task_id}</td>
                  <td className="px-3 py-2.5 text-xs text-foreground max-w-[200px] truncate">{task.title}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-foreground">{task.aircraft}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold tracking-wider ${criticalityStyle[task.criticality]}`}>{task.criticality?.toUpperCase()}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{task.category}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">{task.status?.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{new Date(task.due_date).toLocaleString([], {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedEng && <EngineerModal engineer={selectedEng} onClose={() => setSelectedEng(null)} />}
    </div>
  );
}
